import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { FlagIcon } from "../../constants/languageFlags";
import {
  fetchPollyVoices,
  synthesizePollyVoice,
  DEFAULT_PREVIEW_TEXTS,
} from "../../services/pollyService";
import {
  MicIcon,
  CloseIcon,
  SelectedCheckIcon,
  SoundWaveAnimatedIcon,
  PlayCircleIcon,
  SearchIcon,
  SpinnerCircleIcon,
  AlertCircleIcon,
  EmptyStateIcon,
  SaveCheckIcon,
  SmallCheckCirclePillIcon,
} from "../../icons";
import {
  GENDER_FILTERS,
  ENGINE_FILTERS,
  AVATAR_PALETTE,
  ENGINE_META,
  GENDER_META,
} from "../../constants";
import "./VoiceSettingsModal.scss";

function VoiceCard({ voice, isSelected, onSelect, onPreview, isPreviewing }) {
  const av = AVATAR_PALETTE[voice.gender] || {
    bg: "#f1f5f9",
    text: "#475569",
    border: "#cbd5e1",
  };
  const eng = ENGINE_META[voice.engine] || ENGINE_META.Standard;
  const gen = GENDER_META[voice.gender] || GENDER_META.Male;

  const handlePreview = (e) => {
    e.stopPropagation(); // prevent card select when clicking preview
    onPreview(voice);
  };

  return (
    <button
      type="button"
      className={`vsm-voice-card${isSelected ? " vsm-voice-card--active" : ""}`}
      onClick={() => onSelect(voice)}
      aria-pressed={isSelected}
      aria-label={`${voice.name}, ${voice.gender}, ${voice.engine}`}
    >
      {isSelected && (
        <span className="vsm-voice-check" aria-hidden="true">
          <SelectedCheckIcon size={14} />
        </span>
      )}

      {/* Avatar */}
      <div
        className="vsm-voice-avatar"
        style={{
          background: av.bg,
          color: av.text,
          border: `1.5px solid ${av.border}`,
        }}
      >
        {voice.name.charAt(0)}
      </div>

      {/* Name */}
      <div className="vsm-voice-name">{voice.name}</div>

      {/* Badges */}
      <div className="vsm-voice-badges">
        <span
          className="vsm-badge"
          style={{
            background: gen.bg,
            color: gen.text,
            borderColor: gen.border,
          }}
        >
          {gen.symbol} {voice.gender}
        </span>
        <span
          className="vsm-badge"
          style={{
            background: eng.bg,
            color: eng.text,
            borderColor: eng.border,
          }}
        >
          {voice.engine}
        </span>
      </div>

      {/* Style tag */}
      <div className="vsm-voice-style">{voice.style}</div>

      {/* ── Preview button ── */}
      <button
        type="button"
        className={`vsm-preview-btn${isPreviewing ? " vsm-preview-btn--playing" : ""}`}
        onClick={handlePreview}
        aria-label={isPreviewing ? `Playing ${voice.name}` : `Preview ${voice.name}`}
        title={isPreviewing ? "Playing…" : "Preview voice"}
      >
        {isPreviewing ? <SoundWaveAnimatedIcon size={13} /> : <PlayCircleIcon size={13} />}
        {isPreviewing ? "Playing…" : "Preview"}
      </button>
    </button>
  );
}

function VoiceSettingsModal({
  open,
  onClose,
  languages,
  selectedVoices,
  onSave,
  prefetchedVoices = {},
}) {
  const [activeLang, setActiveLang] = useState(languages[0] || "");
  const [localVoices, setLocalVoices] = useState({});
  const [genderFilter, setGenderFilter] = useState("All");
  const [engineFilter, setEngineFilter] = useState("All");
  const [search, setSearch] = useState("");

  // Voice data fetched live from AWS Polly Lambda
  const [pollyVoices, setPollyVoices] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Tracks which voice (by id_engine key) is currently being previewed
  const [previewingId, setPreviewingId] = useState(null);

  // Editable preview text per language (max 20 chars), pre-filled with language defaults
  const [previewTexts, setPreviewTexts] = useState({});

  const backdropRef = useRef(null);
  const searchRef = useRef(null);

  // Load voices and reset UI state whenever the modal opens
  useEffect(() => {
    if (!open) return;

    // Reset UI state
    setLocalVoices({ ...(selectedVoices || {}) });
    setActiveLang(languages[0] || "");
    setGenderFilter("All");
    setEngineFilter("All");
    setSearch("");
    setFetchError(null);

    // Pre-fill preview texts with language-specific defaults
    const defaultTexts = {};
    languages.forEach((lang) => {
      defaultTexts[lang] = DEFAULT_PREVIEW_TEXTS[lang] || "Hello!";
    });
    setPreviewTexts(defaultTexts);

    // Use voices pre-fetched on page load if available; otherwise fetch now
    if (prefetchedVoices && Object.keys(prefetchedVoices).length > 0) {
      setPollyVoices(prefetchedVoices);
      setLoading(false);
      setTimeout(() => searchRef.current?.focus(), 80);
    } else {
      const loadVoices = async () => {
        setLoading(true);
        try {
          const data = await fetchPollyVoices(languages);
          setPollyVoices(data);
        } catch (err) {
          console.error("❌ [Polly] Failed to fetch voices:", err);
          // eslint-disable-next-line no-console
          setFetchError("Failed to load voices from AWS Polly. Please try again.");
        } finally {
          setLoading(false);
          setTimeout(() => searchRef.current?.focus(), 80);
        }
      };
      loadVoices();
    }
  }, [open]); // eslint-disable-line

  // Escape key handler
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  // Filter voices for the active language based on current gender/engine/search filters
  const allVoicesForLang = pollyVoices[activeLang] || [];
  const filteredVoices = allVoicesForLang.filter((v) => {
    if (genderFilter !== "All" && v.gender !== genderFilter) return false;
    if (engineFilter !== "All" && v.engine !== engineFilter) return false;
    if (search && !v.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const configuredCount = languages.filter((lang) => localVoices[lang]?.id).length;

  const selectLang = (lang) => {
    setActiveLang(lang);
    setSearch("");
    setGenderFilter("All");
    setEngineFilter("All");
  };

  // Unique key per voice combining id + engine — e.g. "Bianca_Neural", "Bianca_Standard"
  const voiceKey = (v) => `${v.id}_${v.engine}`;

  const selectVoice = (voice) => setLocalVoices((prev) => ({ ...prev, [activeLang]: voice }));

  // Previews a voice by calling AWS Polly Lambda to synthesize and play audio
  const handlePreview = async (voice) => {
    const key = voiceKey(voice); // e.g. "Bianca_Neural"
    if (previewingId === key) {
      setPreviewingId(null);
      return;
    }
    const text = previewTexts[activeLang] || DEFAULT_PREVIEW_TEXTS[activeLang] || "Hello!";
    const engine = voice.engine?.toLowerCase() || "standard";
    setPreviewingId(key);
    try {
      await synthesizePollyVoice(voice.id, text, engine);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("❌ [Preview] Failed:", err);
    } finally {
      setPreviewingId(null);
    }
  };

  // Updates editable preview text for the active language (max 20 chars)
  const handlePreviewTextChange = (e) => {
    const val = e.target.value.slice(0, 20); // enforce max 20 chars
    setPreviewTexts((prev) => ({ ...prev, [activeLang]: val }));
  };

  const handleSave = () => {
    onSave(localVoices);
    onClose();
  };
  const handleReset = () => setLocalVoices({});

  const handleBackdrop = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  return (
    <div className="vsm-backdrop" ref={backdropRef} onClick={handleBackdrop} role="presentation">
      <div className="vsm-modal" role="dialog" aria-modal="true" aria-labelledby="vsm-title">
        {/* ── Header ─────────────────────────────────────────── */}
        <header className="vsm-header">
          <div className="vsm-header-left">
            <div className="vsm-header-icon-wrap">
              <MicIcon size={20} />
            </div>
            <div className="vsm-header-text">
              <h2 id="vsm-title" className="vsm-title">
                Voice Preferences
              </h2>
              <p className="vsm-subtitle">
                Assign a preferred TTS voice per language — saved to your profile
              </p>
            </div>
          </div>

          <div className="vsm-header-right">
            <div className="vsm-aws-badge">
              <span className="vsm-aws-dot" />
              AWS Polly · Web Speech Preview
            </div>
            <button className="vsm-close-btn" onClick={onClose} aria-label="Close modal">
              <CloseIcon size={14} />
            </button>
          </div>
        </header>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="vsm-body">
          {/* Sidebar — language list */}
          <aside className="vsm-sidebar">
            <div className="vsm-sidebar-heading">
              Languages
              <span className="vsm-sidebar-count">{languages.length}</span>
            </div>

            <ul className="vsm-lang-list" role="listbox" aria-label="Language list">
              {languages.map((lang) => {
                const voice = localVoices[lang];
                const isActive = lang === activeLang;
                return (
                  <li
                    key={lang}
                    role="option"
                    aria-selected={isActive}
                    className={[
                      "vsm-lang-item",
                      isActive ? "vsm-lang-item--active" : "",
                      voice ? "vsm-lang-item--configured" : "",
                    ].join(" ")}
                    onClick={() => selectLang(lang)}
                  >
                    <span className="vsm-lang-flag" aria-hidden="true">
                      <FlagIcon language={lang} size={20} />
                    </span>
                    <div className="vsm-lang-info">
                      <span className="vsm-lang-name">{lang}</span>
                      {voice ? (
                        <span className="vsm-lang-voice-label">
                          {voice.name}
                          <span className="vsm-lang-engine-dot" />
                          {voice.engine}
                        </span>
                      ) : (
                        <span className="vsm-lang-unset">No voice selected</span>
                      )}
                    </div>
                    {voice ? (
                      <span className="vsm-lang-configured-badge" aria-label="Configured">
                        ✓
                      </span>
                    ) : (
                      <span className="vsm-lang-arrow">›</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Main Panel */}
          <div className="vsm-panel">
            {/* Panel header */}
            <div className="vsm-panel-header">
              <div className="vsm-panel-title-row">
                <span className="vsm-panel-flag" aria-hidden="true">
                  <FlagIcon language={activeLang} size={22} />
                </span>
                <span className="vsm-panel-lang">{activeLang}</span>
                <span className="vsm-panel-voice-count">
                  {allVoicesForLang.length} voices available
                </span>
              </div>

              {localVoices[activeLang] && (
                <div className="vsm-panel-selected-pill">
                  <SmallCheckCirclePillIcon size={10} />
                  <span>Selected:</span>
                  <strong>{localVoices[activeLang].name}</strong>
                  <span className="vsm-pill-sep">·</span>
                  <span>{localVoices[activeLang].engine}</span>
                  <span className="vsm-pill-sep">·</span>
                  <span>{localVoices[activeLang].gender}</span>
                </div>
              )}
            </div>

            {/* Filter bar */}
            <div className="vsm-filter-bar">
              <div className="vsm-filter-group">
                <span className="vsm-filter-label">Gender</span>
                {GENDER_FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`vsm-filter-btn${genderFilter === f ? " vsm-filter-btn--active" : ""}`}
                    onClick={() => setGenderFilter(f)}
                  >
                    {f === "Female" ? "♀ Female" : f === "Male" ? "♂ Male" : "All"}
                  </button>
                ))}
              </div>

              <div className="vsm-filter-group">
                <span className="vsm-filter-label">Engine</span>
                {ENGINE_FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`vsm-filter-btn${engineFilter === f ? " vsm-filter-btn--active" : ""}`}
                    onClick={() => setEngineFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="vsm-search-wrap">
                <SearchIcon size={14} className="vsm-search-icon" />
                <input
                  ref={searchRef}
                  className="vsm-search"
                  type="text"
                  placeholder="Search voices…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search voices"
                />
                {search && (
                  <button
                    className="vsm-search-clear"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* ── Editable preview text input per language ── */}
            <div className="vsm-preview-text-bar">
              <label className="vsm-preview-text-label" htmlFor="vsm-preview-text">
                <PlayCircleIcon size={13} />
                Preview text
              </label>
              <div className="vsm-preview-text-wrap">
                <input
                  id="vsm-preview-text"
                  className="vsm-preview-text-input"
                  type="text"
                  value={previewTexts[activeLang] || ""}
                  onChange={handlePreviewTextChange}
                  maxLength={20}
                  placeholder="Type preview text…"
                  aria-label={`Preview text for ${activeLang}`}
                />
                <span className="vsm-preview-text-counter">
                  {(previewTexts[activeLang] || "").length}/20
                </span>
              </div>
              <span className="vsm-preview-text-hint">
                Click ▶ Preview on any voice card to hear it
              </span>
            </div>

            {/* ── Loading state ── */}
            {loading && (
              <div className="vsm-loading-state">
                <SpinnerCircleIcon size={28} className="vsm-spinner" />
                <p>Loading voices from AWS Polly…</p>
              </div>
            )}

            {/* ── Error state ── */}
            {!loading && fetchError && (
              <div className="vsm-error-state">
                <AlertCircleIcon size={28} color="#ef4444" />
                <p>{fetchError}</p>
                <button
                  className="vsm-retry-btn"
                  onClick={() => {
                    setFetchError(null);
                    setLoading(true);
                    fetchPollyVoices(languages)
                      .then(setPollyVoices)
                      .catch((err) => {
                        // eslint-disable-next-line no-console
                        console.error("❌ [Polly] Retry failed:", err);
                        setFetchError("Failed to load voices. Please try again.");
                      })
                      .finally(() => setLoading(false));
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* ── Voice grid ── */}
            {!loading && !fetchError && (
              <div className="vsm-voice-grid" role="group" aria-label="Available voices">
                {filteredVoices.length === 0 ? (
                  <div className="vsm-empty-state">
                    <EmptyStateIcon size={40} />
                    <p>No voices match your filters.</p>
                    <button
                      className="vsm-reset-filter-btn"
                      onClick={() => {
                        setGenderFilter("All");
                        setEngineFilter("All");
                        setSearch("");
                      }}
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  filteredVoices.map((voice) => (
                    <VoiceCard
                      key={voiceKey(voice)}
                      voice={voice}
                      isSelected={
                        // Match BOTH name AND engine — "Bianca_Neural" ≠ "Bianca_Standard"
                        localVoices[activeLang] != null &&
                        voiceKey(localVoices[activeLang]) === voiceKey(voice)
                      }
                      onSelect={selectVoice}
                      onPreview={handlePreview}
                      isPreviewing={previewingId === voiceKey(voice)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <footer className="vsm-footer">
          <div className="vsm-footer-summary">
            <span
              className={`vsm-summary-dot${configuredCount === languages.length ? " vsm-summary-dot--complete" : ""}`}
            />
            <span>
              <strong>{configuredCount}</strong> of <strong>{languages.length}</strong> languages
              configured
            </span>
            {configuredCount === languages.length && (
              <span className="vsm-all-set-badge">All set!</span>
            )}
          </div>

          <div className="vsm-footer-actions">
            <button
              type="button"
              className="vsm-btn vsm-btn--reset"
              onClick={handleReset}
              disabled={configuredCount === 0}
            >
              Reset All
            </button>
            <button type="button" className="vsm-btn vsm-btn--cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="vsm-btn vsm-btn--save" onClick={handleSave}>
              <SaveCheckIcon size={14} />
              Save Preferences
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

VoiceCard.propTypes = {
  voice: PropTypes.shape({
    name: PropTypes.string,
    gender: PropTypes.string,
    engine: PropTypes.string,
    style: PropTypes.string,
    id: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  onPreview: PropTypes.func,
  isPreviewing: PropTypes.bool,
};

VoiceSettingsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  languages: PropTypes.arrayOf(PropTypes.string),
  selectedVoices: PropTypes.object,
  onSave: PropTypes.func,
  prefetchedVoices: PropTypes.object,
};

export default VoiceSettingsModal;
